/**
 * journeyBridge.js - one-way bridge from the ANONYMOUS marketing journey
 * (localStorage 5-stage + project, see journeyResolver.js) into the
 * LOGGED-IN workspace scenario (profiles.build_type + building_stage).
 *
 * The two systems stay separate and independent. This bridge only reads
 * the anonymous journey at the moment of sign-in / sign-up so a visitor
 * lands in the workspace on the exact same project + stage they were
 * exploring anonymously. It never runs for an already-onboarded user
 * (see AuthContext seeding guard).
 */
import { resolveJourney } from '../../data/journeyResolver';
import { getStagesForProjectType } from './onboardingData';

// Marketing project slug -> workspace build_type (PROJECT_TYPES value).
const SLUG_TO_BUILD_TYPE = {
  'swimming-pools': 'Pool',
  'decking': 'Deck',
  'landscaping': 'Landscaping',
  'verandahs-pergolas': 'Pergola',
  'renovations': 'Renovation',
  'building-a-new-home': 'New Home',
  'knockdown-rebuild': 'New Home',
  'house-and-land-adelaide': 'New Home',
  'first-home-buyer-adelaide': 'New Home',
  'choosing-land': 'New Home',
  'home-design': 'New Home',
  'property-development': 'New Home',
  'changing-builders': 'New Home',
  'sheds-garages': 'Shed / Garage',
  'interior-design': 'Interior',
};

// Label-aware anchors so the coarse 5-stage lands on the closest real
// milestone in each project's granular stage list (not just a position).
// Every list starts with "Thinking of building" (dream). Falls back to a
// positional anchor when a project has no matching milestone.
const RX = {
  // Design / drawings / planning phase.
  plan: /design|drawing|master plan|\bplans\b|assessment|pool type|kit selection/i,
  // Choosing / committing to a pro.
  choose: /quote|builder selection|landscaper selection|pool builder|signed up|contractor/i,
  // Active construction milestones.
  build: /footing|subframe|bearer|\bboards\b|balustrade|slab|frame|lockup|first fix|excavation|shell|plumbing|coping|\bposts?\b|beams|roofing|cladding|structural|rough in|hardscape|planting|turf|installation|drainage/i,
  // Living / handover, in priority order (best fit first).
  own: [/living in|first 30/i, /handover|commission/i, /establishment|maintenance/i, /finishing/i, /practical completion/i],
};

function firstMatch(stages, rx) {
  const i = stages.findIndex((s) => rx.test(s));
  return i >= 0 ? stages[i] : null;
}

function midMatch(stages, rx) {
  const idxs = stages.map((s, i) => (rx.test(s) ? i : -1)).filter((i) => i >= 0);
  return idxs.length ? stages[idxs[Math.floor(idxs.length / 2)]] : null;
}

function stageForList(stageKey, stages) {
  if (!Array.isArray(stages) || !stages.length) return null;
  const last = stages.length - 1;
  switch (stageKey) {
    case 'dream':  return stages[0];
    case 'plan':   return firstMatch(stages, RX.plan) || stages[Math.min(1, last)];
    case 'choose': return firstMatch(stages, RX.choose) || stages[Math.min(2, last)];
    case 'build':  return midMatch(stages, RX.build) || stages[Math.round(last / 2)];
    case 'own':    return RX.own.reduce((acc, rx) => acc || firstMatch(stages, rx), null) || stages[last];
    default:       return stages[0];
  }
}

/**
 * Read the resolved anonymous journey and translate it into the workspace
 * seed fields. Returns { build_type, building_stage } or null when there
 * is no confident anonymous journey to carry over.
 */
export function resolveWorkspaceSeedFromAnon() {
  let resolved;
  try { resolved = resolveJourney(); } catch { return null; }
  if (!resolved || !resolved.project || !resolved.stage) return null;
  const build_type = SLUG_TO_BUILD_TYPE[resolved.project.slug];
  if (!build_type) return null;
  const building_stage = stageForList(resolved.stage, getStagesForProjectType(build_type));
  if (!building_stage) return null;
  return { build_type, building_stage };
}
